import { useEffect, useState } from "react"
import useUserRedemptions from "~hooks/useUserRedemptions"
import { PerkShape } from "~shapes/perk"
import { GetCompanyOptionsShape } from "~shapes/company"
import { STATUS } from "~constants/status"
import usePerks from "~hooks/usePerks"
import { toast } from "react-toastify"
import PerksModal from "~components/users/PerksModal"
import RedeemModal from "~components/redemption/RedeemModal"
import { ContainerRight } from "~styled/containers"
import { ButtonPrimarySoftLarge } from "~styled/button"
import UserRedemptionsList from "~components/users/UserRedemptionsList"

interface Props {
  userId: string
  userEmail: string
}
const Redemption = ({ userId, userEmail }: Props) => {
  const [isPerksModalOpened, setIsPerksModalOpened] = useState<boolean>(false)
  const [isRedeemModalOpened, setIsRedeemModalOpened] = useState<boolean>(false)
  const [selectedPerk, setSelectedPerk] = useState<PerkShape | undefined>()

  const [options, setOptions] = useState<GetCompanyOptionsShape | undefined>(
    undefined,
  )
  const tableOptions = { sort: "name", size: 40, status: STATUS.ACTIVE }

  const {
    data: redemptions,
    refetch: fetchRedemptions,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isError: errorFetchingRedemptions,
  } = useUserRedemptions(userId)

  const {
    data: perksData,
    refetch: fetchPerks,
    isFetching: isLoadingPerks,
  } = usePerks({ ...tableOptions, ...options })

  useEffect(() => {
    if (errorFetchingRedemptions) {
      toast.error(`Failed to load user redemptions`)
    }
  }, [errorFetchingRedemptions])

  useEffect(() => {
    if (!userId) return
    ;(async () => {
      await fetchRedemptions()
    })()
  }, [userId])

  const onOpenModal = async () => {
    setIsPerksModalOpened(true)
    await fetchPerks()
  }

  const onCloseModal = async () => {
    setIsPerksModalOpened(false)
  }

  useEffect(() => {
    if (isPerksModalOpened) {
      ;(async () => {
        await fetchPerks()
      })()
    }
  }, [options])

  const onSelectPerk = async (perk: PerkShape) => {
    setSelectedPerk(perk)
    if (
      confirm(
        `Are you absolutely certain you wish to redeem this perk on behalf of the user ${userEmail}?\n` +
          "\n" +
          "Proceeding with this action will finalize the redemption process and cannot be reverted. Please ensure that you have received confirmation from the user before proceeding.",
      )
    ) {
      setIsPerksModalOpened(false)
      setIsRedeemModalOpened(true)
    }
  }

  const onRedeemModalClose = async (redeemed: boolean) => {
    setIsRedeemModalOpened(false)
    if (redeemed) await fetchRedemptions()
  }

  return (
    <>
      <PerksModal
        isLoading={isLoadingPerks}
        isOpened={isPerksModalOpened}
        perks={perksData?.result}
        onRequestClose={onCloseModal}
        onRequestConfirm={onCloseModal}
        search={el => setOptions(el)}
        onSelectPerk={onSelectPerk}
      />

      <RedeemModal
        closeModal={onRedeemModalClose}
        isOpened={isRedeemModalOpened}
        perk={selectedPerk}
        userId={userId}
      />

      <ContainerRight style={{ marginBottom: 30 }}>
        <ButtonPrimarySoftLarge onClick={onOpenModal}>
          Redeem perk
        </ButtonPrimarySoftLarge>
      </ContainerRight>

      <UserRedemptionsList
        redemptions={
          redemptions?.pages.flatMap((page: any) => page.result) ?? []
        }
        isLoading={isFetching}
        fetchNextPage={fetchNextPage}
        hasNextPage={
          redemptions?.pages[redemptions.pages.length - 1]?.hasNextPage
        }
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  )
}

export default Redemption
